import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { api } from "@/lib/api-client"
import type { ReportType, SavedReport, ReportResult, CustomReportEntity, CustomReportConfig, CustomReportResult } from "@/lib/types"

export function useReportTypes() {
  return useQuery({
    queryKey: ["reports", "types"],
    queryFn: () => api.get<{ data: ReportType[] }>("/api/v1/reports/types"),
  })
}

export function useReport(type: string, params?: Record<string, string>) {
  const searchParams = new URLSearchParams(params)
  const queryString = searchParams.toString()
  return useQuery({
    queryKey: ["reports", type, params],
    queryFn: () => api.get<ReportResult>(`/api/v1/reports/${type}${queryString ? `?${queryString}` : ""}`),
    enabled: !!type,
  })
}

export function useSavedReports() {
  return useQuery({
    queryKey: ["reports", "saved"],
    queryFn: () => api.get<{ data: SavedReport[] }>("/api/v1/reports/saved"),
  })
}

export function useSaveReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; report_type: string; config: Record<string, unknown> }) =>
      api.post<{ data: SavedReport }>("/api/v1/reports/saved", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "saved"] })
    },
  })
}

export function useDeleteSavedReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/reports/saved/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "saved"] })
    },
  })
}

export function useCustomReportEntities() {
  return useQuery({
    queryKey: ["reports", "custom", "entities"],
    queryFn: () => api.get<{ data: CustomReportEntity[] }>("/api/v1/reports/custom/entities"),
  })
}

export function useCustomReportPreview() {
  return useMutation({
    mutationFn: (config: CustomReportConfig) =>
      api.post<CustomReportResult>("/api/v1/reports/custom/preview", { config }),
  })
}

export function useReportExport() {
  return useCallback(async (type: string, params?: Record<string, string>) => {
    const searchParams = new URLSearchParams(params)
    const queryString = searchParams.toString()
    const url = `/api/v1/reports/${type}/export${queryString ? `?${queryString}` : ""}`

    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/csv",
      },
    })

    if (!response.ok) {
      throw new Error("Export failed")
    }

    const blob = await response.blob()
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = downloadUrl
    a.download = `raport_${type}_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(downloadUrl)
  }, [])
}
